import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '红桃视频',
    url: 'https://hongtaovideo.com',
    description: '中国领先的高清短视频平台',
    foundingDate: '2019',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '浙江省杭州市余杭区未来科技城红桃大厦18层',
      addressLocality: '杭州',
      addressRegion: '浙江',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-6666',
      email: 'contact@hongtaovideo.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '红桃视频是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，红桃视频完全免费。用户可以免费观看所有短视频内容，无需支付任何费用。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在红桃视频上发布视频？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '下载红桃视频App后，注册账号即可开始创作。点击底部"+"按钮，选择拍摄或上传视频，编辑后即可发布。',
        },
      },
      {
        '@type': 'Question',
        name: '红桃视频支持哪些设备？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '红桃视频支持iOS 15+和Android 8+系统的手机设备，同时提供网页版可在电脑浏览器中访问。',
        },
      },
      {
        '@type': 'Question',
        name: '创作者如何获得收益？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '创作者可以通过广告分成、直播打赏、品牌合作、电商带货等多种方式在红桃视频平台获得收益。达到一定粉丝量后即可开通相应变现功能。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于红桃视频 - 品牌介绍、发展历程与联系方式"
        description="红桃视频成立于2019年，是中国领先的高清短视频平台，累计用户超5亿。了解红桃视频品牌故事、发展历程、用户口碑及联系方式。"
        keywords="红桃视频,关于红桃视频,红桃视频介绍,红桃视频联系方式,短视频平台"
        canonicalUrl="https://hongtaovideo.com/about"
        jsonLd={{ ...orgJsonLd, ...faqJsonLd }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="relative py-16 md:py-20 bg-background-50 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-50/50 to-transparent"></div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于红桃视频
              </h1>
              <p className="text-lg text-primary-500 font-heading font-semibold mb-4">
                {aboutData.brandSlogan}
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                {aboutData.introduction}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 mb-16">
              {aboutData.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-foreground-950 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                <div className="flex flex-col gap-8">
                  {aboutData.milestones.map((milestone, index) => (
                    <div
                      key={milestone.year}
                      className={`relative flex items-start gap-6 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'} pl-12 md:pl-0`}>
                        <div className="bg-background-100 rounded-xl p-4 border border-background-200/70">
                          <span className="inline-block px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full mb-2">
                            {milestone.year}
                          </span>
                          <h3 className="text-base font-semibold text-foreground-950 mb-1">{milestone.title}</h3>
                          <p className="text-xs text-foreground-600">{milestone.description}</p>
                        </div>
                      </div>
                      <div className="absolute left-2 md:left-1/2 w-5 h-5 rounded-full bg-primary-500 border-4 border-background-50 md:-translate-x-1/2 mt-3"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              创作者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {aboutData.testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={t.avatarUrl}
                        alt={t.name}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">{t.name}</h3>
                      <p className="text-xs text-foreground-500">{t.title}</p>
                      <p className="text-xs text-primary-500 mt-0.5">{t.followers} 粉丝</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto flex flex-col gap-3">
              {[
                { q: '红桃视频是免费的吗？', a: '是的，红桃视频完全免费。用户可以免费观看所有短视频内容，无需支付任何费用。' },
                { q: '如何在红桃视频上发布视频？', a: '下载红桃视频App后，注册账号即可开始创作。点击底部"+"按钮，选择拍摄或上传视频，编辑后即可发布。' },
                { q: '红桃视频支持哪些设备？', a: '红桃视频支持iOS 15+和Android 8+系统的手机设备，同时提供网页版可在电脑浏览器中访问。' },
                { q: '创作者如何获得收益？', a: '创作者可以通过广告分成、直播打赏、品牌合作、电商带货等多种方式在红桃视频平台获得收益。' },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-xl border border-background-200/70">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <h3 className="text-sm font-semibold text-foreground-950 pr-4">{faq.q}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              联系我们
            </h2>
            <div className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-map-pin-line text-accent-600 text-lg"></i>
                  </div>
                  <h3 className="text-xs font-semibold text-foreground-950 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{aboutData.contactInfo.address}</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-phone-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-xs font-semibold text-foreground-950 mb-1">客服电话</h3>
                  <p className="text-sm font-semibold text-primary-500">{aboutData.contactInfo.phone}</p>
                  <p className="text-xs text-foreground-500 mt-1">{aboutData.contactInfo.businessHours}</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-secondary-600 text-lg"></i>
                  </div>
                  <h3 className="text-xs font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-600">{aboutData.contactInfo.email}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}